'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

const CONSENT_KEY = 'safeplay_consent_v1';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

/**
 * Detect user's region based on browser locale and timezone
 * No external API calls — uses navigator.language and Intl API only
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  // EU timezone prefixes
  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  // US timezone prefixes
  const usTZPrefixes = ['America/'];

  // EU country codes
  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS =
    usTZPrefixes.some((p) => tz.startsWith(p)) &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Get consent record from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error('Failed to parse consent record:', e);
    return null;
  }
}

/**
 * Save consent record to localStorage
 */
export function saveConsent(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (e) {
    console.error('Failed to save consent record:', e);
  }
}

/**
 * Create a new consent record
 */
export function createConsentRecord(
  region: ConsentRegion,
  choices: ConsentRecord['choices']
): ConsentRecord {
  return {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

/**
 * Check if a specific consent category is enabled
 */
export function isConsentGiven(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return false;
  return consent.choices[category] === true;
}

/**
 * Clear consent record from localStorage
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (e) {
    console.error('Failed to clear consent record:', e);
  }
}
